import apiClient from '@/lib/api-client';
import { WorkspaceMember, WorkspaceRole } from '../../../types/workspace';

export interface InviteMemberData {
  email: string;
  role: WorkspaceRole;
}

export interface UpdateMemberRoleData {
  role: WorkspaceRole;
}

export const membersApi = {
  /**
   * Get all members of a workspace
   */
  getMembers: async (workspaceId: string): Promise<WorkspaceMember[]> => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/members`);
    return response.data;
  },

  /**
   * Invite a new member to the workspace
   */
  inviteMember: async (workspaceId: string, data: InviteMemberData): Promise<any> => {
    const response = await apiClient.post(`/workspaces/${workspaceId}/members/invite`, data);
    return response.data;
  },

  /**
   * Update a member's role
   */
  updateMemberRole: async (
    workspaceId: string,
    memberId: string,
    data: UpdateMemberRoleData
  ): Promise<WorkspaceMember> => {
    const response = await apiClient.patch(
      `/workspaces/${workspaceId}/members/${memberId}`,
      data
    );
    return response.data;
  },

  /**
   * Remove a member from the workspace
   */
  removeMember: async (workspaceId: string, memberId: string): Promise<void> => {
    await apiClient.delete(`/workspaces/${workspaceId}/members/${memberId}`);
  },

  /**
   * Get pending invitations for a workspace
   */
  getInvitations: async (workspaceId: string): Promise<any[]> => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/invitations`);
    
    // Handle nested response structure: response.data.data.data.invitations
    let invitations = null;
    if (Array.isArray(response.data?.data?.data?.invitations)) {
      invitations = response.data.data.data.invitations;
    } else if (Array.isArray(response.data?.data?.invitations)) {
      invitations = response.data.data.invitations;
    } else if (Array.isArray(response.data?.data)) {
      invitations = response.data.data;
    } else if (Array.isArray(response.data)) {
      invitations = response.data;
    }
    
    // Ensure we always return an array
    if (!Array.isArray(invitations)) {
      console.error('[getInvitations] Response is not an array');
      return [];
    }
    return invitations;
  },

  /**
   * Revoke a pending invitation
   */
  revokeInvitation: async (workspaceId: string, invitationId: string): Promise<void> => {
    await apiClient.delete(`/workspaces/${workspaceId}/invitations/${invitationId}`);
  },

  /**
   * Get pending invitations for the current user
   */
  getMyInvitations: async (): Promise<{ success: boolean; data: { invitations: any[] } }> => {
    const response = await apiClient.get('/invitations/pending');
    return response.data;
  },

  /**
   * Decline an invitation
   */
  declineInvitation: async (invitationId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post(`/invitations/${invitationId}/decline`);
    return response.data;
  },

  /**
   * Accept an invitation
   */
  acceptInvitation: async (token: string): Promise<{ success: boolean; data: { member: WorkspaceMember }; message: string }> => {
    const response = await apiClient.post(`/invitations/${token}/accept`);
    return response.data;
  },
};
